import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ButtonItem } from './split-button.model';
import { MenuItemCommandEvent } from 'primeng/api';

@Component({
  selector: 'lib-split-button',
  templateUrl: './split-button.component.html',
  styleUrls: ['./split-button.component.scss']
})
export class SplitButtonComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public loadingTopic = '';
  @Input() public collapsed = false;
  @Input() public appendTo: string = null;
  @Input() public items: ButtonItem[] = [];

  public optionButtons: ButtonItem[];
  public primaryButton: ButtonItem;
  public singleItem = false;
  public loading = false;
  private subscriptions = new Subscription();

  constructor(private translateService: TranslateService) {}

  public ngOnInit(): void {
    if (this.items) {
      this.constructButtons(this.items);
    }

    this.subscriptions.add(
      this.translateService.onLangChange.subscribe(() => {
        this.constructButtons(this.items);
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ((changes.items || this.collapsed) && this.compareIfButtonsChanged(changes)) {
      this.constructButtons(this.items);
    }
  }

  public onButtonClick(event: Event): void {
    if (this.primaryButton?.disabled || this.loading) {
      return;
    }
    const commandEvent: MenuItemCommandEvent = {
      originalEvent: event
    };
    this.primaryButton.command(commandEvent);
  }

  private constructButtons(buttons: ButtonItem[]): void {
    const mappedButtons = buttons.map((button) => this.translateLabel(button));

    mappedButtons.forEach((button) => {
      button.visible ??= true;

      // default the disabled state, if the button is not visible it is also not enabled
      // needed because the primary button determination might select a non-visible button
      if (!button.disabled) {
        button.disabled = !button.visible;
      }
    });

    this.primaryButton = this.getPrimaryButton(mappedButtons);

    if (this.collapsed) {
      this.optionButtons = mappedButtons;
    } else {
      this.optionButtons = mappedButtons.filter((button) => this.isOptionVisible(button));
      this.singleItem = this.optionButtons.length === 0;
    }
  }

  private translateLabel(button: ButtonItem): ButtonItem {
    return {
      ...button,
      label: this.translateService.instant(button.label)
    };
  }

  private isOptionVisible(button: ButtonItem): boolean {
    return button !== this.primaryButton && this.isButtonVisible(button);
  }

  private isButtonEnabled(button: ButtonItem): boolean {
    return button?.disabled !== true || button?.disabled === undefined;
  }

  private isButtonVisible(button: ButtonItem): boolean {
    return button?.visible === true || button?.visible === undefined;
  }

  private isButtonPrimaryEligible(button: ButtonItem): boolean {
    return this.isButtonVisible(button) && this.isButtonEnabled(button);
  }

  private getPrimaryButton(buttons: ButtonItem[]): ButtonItem {
    // If no buttons are available, return an empty object - this is an implementation error
    if (buttons.length === 0) {
      return { label: '', command: () => {} } as ButtonItem; // Return an empty object if no buttons are available
    }
    // if only one button is available, it will be the primary button
    if (buttons.length === 1) {
      return buttons[0];
    }
    // if there is a primary button, it will be the primary button
    return (
      buttons.find((button) => button.primary && this.isButtonVisible(button)) || // if a button is marked as primary and visible use it (the first one in the list)
      buttons.find((button) => this.isButtonPrimaryEligible(button)) || // else if no button is marked as primary, use the first visible and enabled button
      buttons.find((button) => this.isButtonVisible(button)) || // else if no button is enabled, take the first visible button
      buttons[0] // else if no button is visible, take the first button that is available (this is an implementation error)
    );
  }

  private compareIfButtonsChanged(changes: SimpleChanges): boolean {
    if (changes.items) {
      const previousValue = changes.items.previousValue;
      const currentValue = changes.items.currentValue;
      return JSON.stringify(previousValue) !== JSON.stringify(currentValue);
    }

    return true;
  }
}
